import type { ReactNode } from "react";

import { Box, Column, Row, Text } from "@hightouch/htui";

type PropertyRowProps = {
  label: string;
  children: ReactNode;
  /** Stacks the value under the label, for lists such as reviewers. */
  stacked?: boolean;
  /** Controls on the label's line, at its end. Only a stacked row shows them. */
  action?: ReactNode;
};

/** One labelled property in a detail page's sidebar. */
export const PropertyRow = ({
  label,
  children,
  stacked = false,
  action,
}: PropertyRowProps) =>
  stacked ? (
    <Column gap={1.5}>
      <Row align="center" justify="space-between" gap={2} minHeight={6}>
        <Text size="sm" color="text.tertiary">
          {label}
        </Text>
        {action}
      </Row>
      {children}
    </Column>
  ) : (
    <Row align="center" gap={2} minHeight={8}>
      <Box width={24} flexShrink={0}>
        <Text size="sm" color="text.tertiary">
          {label}
        </Text>
      </Box>
      <Box flex={1} minWidth={0}>
        {children}
      </Box>
    </Row>
  );

type DetailSectionProps = {
  title: string;
  /** Shown beside the title once it's known. */
  count?: number;
  actions?: ReactNode;
  /** Space under the title row, in the space scale. */
  gap?: 2 | 3;
  children: ReactNode;
};

export const DetailSection = ({
  title,
  count,
  actions,
  gap = 3,
  children,
}: DetailSectionProps) => (
  <Column as="section" gap={gap}>
    <Row align="center" justify="space-between" gap={2} minHeight={6}>
      <Row align="baseline" gap={2}>
        <Text fontWeight="semibold">{title}</Text>
        {count !== undefined && <Text color="text.secondary">{count}</Text>}
      </Row>
      {actions}
    </Row>
    {children}
  </Column>
);

type DetailFieldProps = {
  label: string;
  children: ReactNode;
};

/** One labelled field in a task page's Details, its control filling the row. */
export const DetailField = ({ label, children }: DetailFieldProps) => (
  <Row align="center" gap={2}>
    <Box width={28} flexShrink={0}>
      <Text fontWeight="medium">{label}</Text>
    </Box>
    <Box flex={1} minWidth={0}>
      {children}
    </Box>
  </Row>
);

/** Stands where a list in the details is empty, saying what goes there. */
export const DashedPlaceholder = ({ children }: { children: ReactNode }) => (
  <Box
    px={3}
    py={2}
    bg="white"
    // A fixed 1px: the hairline is 0.5px on high-DPI screens, where a dashed line reads as solid.
    borderWidth="1px"
    borderStyle="dashed"
    borderColor="base.border"
    borderRadius="2xl"
  >
    <Text color="text.disabled">{children}</Text>
  </Box>
);
